import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TextInput } from '@shared/components/forms';
import { getSearchHistory, addToSearchHistory } from '@shared/utils/searchHistory';

export function GlobalStockSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowHistory(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard shortcuts: Cmd+K / Ctrl+K and /
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
        return;
      }

      if (e.key === '/') {
        const tag = (e.target as HTMLElement)?.tagName;
        const isEditable = tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable;
        if (!isEditable) {
          e.preventDefault();
          inputRef.current?.focus();
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    const symbol = query.trim().toUpperCase();
    if (symbol) {
      addToSearchHistory(symbol);
      setQuery('');
      setShowHistory(false);
      navigate(`/market?tab=stock&symbol=${symbol}`);
    }
  }, [query, navigate]);

  const handleHistoryClick = useCallback((symbol: string) => {
    addToSearchHistory(symbol);
    setQuery('');
    setShowHistory(false);
    navigate(`/market?tab=stock&symbol=${symbol}`);
  }, [navigate]);

  return (
    <form onSubmit={handleSubmit} className="relative w-[260px] focus-within:w-[320px] transition-all duration-200">
      <TextInput
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => { setSearchHistory(getSearchHistory()); setShowHistory(true); }}
        placeholder="Search ticker... (⌘K)"
        size="sm"
        variant="default"
        inputClassName="rounded-md border-border"
        leftIcon={
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
          </svg>
        }
      />
      {showHistory && searchHistory.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute top-[calc(100%+4px)] left-0 right-0 bg-surface border border-border rounded-md shadow-lg overflow-hidden z-50"
        >
          <div className="py-1.5 px-3 text-[10px] font-semibold text-text-muted uppercase tracking-wide border-b border-border">
            Recent
          </div>
          {searchHistory.map((symbol) => (
            <button
              key={symbol}
              type="button"
              onClick={() => handleHistoryClick(symbol)}
              className="block w-full text-left py-1.5 px-3 text-sm text-text bg-transparent border-none cursor-pointer hover:bg-bg-alt transition-colors"
            >
              {symbol}
            </button>
          ))}
        </div>
      )}
    </form>
  );
}
